/**
 * v3.session — /v3/session/* (singular, NOT v3.sessions which is the
 * Sessions/SessionEvents transactional aggregate).
 *
 * Three routes for the CONFIG-03 branch-of-day override feature:
 *   GET    /v3/session/my-context        — aggregate identity + branch
 *   POST   /v3/session/branch-override   — set today's override
 *   DELETE /v3/session/branch-override   — clear today's override
 *
 * Contract documented in `docs/branch-autodetect-design-2026-04-23.md` §4.
 */

import type {
  RequestOptions,
  MyContextResponse,
  SetBranchOverrideRequest,
  SetBranchOverrideResponse,
  ClearBranchOverrideResponse,
} from '@retail-ops/shared-types';
import type { ApiHttpClient } from '../http.js';

/**
 * REQ-82 keyless-cutover diagnostic shape returned by GET /v3/session/whoami.
 * Reports HOW the caller authenticated so the UI can show whether the Teams SSO
 * bearer validated end-to-end (before the function key is removed). No secret
 * value is included — only presence booleans + non-sensitive token claims.
 */
export interface WhoamiResponse {
  success: boolean;
  /** Resolved method for THIS request: 'bearer' | 'function-key' | 'officer-code' | 'none'. */
  authMethod: string;
  hasBearer: boolean;
  bearerValid: boolean;
  bearerError?: string | null;
  /** Decoded (unvalidated) token audience — surfaced so an audience mismatch is visible. */
  tokenAud?: string | null;
  tokenIss?: string | null;
  oid?: string | null;
  upn?: string | null;
  scopes?: ReadonlyArray<string>;
  keyPresent: boolean;
  officerPresent: boolean;
  /** Method recorded by BearerAuthMiddleware — confirms the middleware ran. */
  middlewareMethod?: string | null;
}

export class SessionApi {
  public constructor(private readonly http: ApiHttpClient) {}

  /**
   * REQ-82 keyless-cutover probe. Returns how the caller authenticated for THIS
   * request (incl. whether the Teams SSO bearer cryptographically validated).
   * Anonymous route — works with or without the function key, so it can verify
   * the bearer path before the key is removed from the bundle.
   */
  public whoami(opts?: RequestOptions): Promise<WhoamiResponse> {
    return this.http.get<WhoamiResponse>('/v3/session/whoami', { ...opts });
  }

  /**
   * REQ-82 keyless-cutover — POST the frontend Teams SSO probe result so it's
   * logged server-side (App Insights), letting us verify the cutover without
   * transcribing the on-screen badge. Body carries presence booleans +
   * clientType + upn + the getTeamsAuthToken() error text (no secret).
   */
  public logSsoProbe(
    probe: Record<string, unknown>,
    opts?: RequestOptions
  ): Promise<{ success: boolean }> {
    return this.http.post<{ success: boolean }>('/v3/session/sso-probe', {
      ...opts,
      body: probe,
    });
  }

  /**
   * Fetch the merged identity + branch envelope for the calling user.
   * Returns `{ success, user, branch, status }`. AuthContext consumes this
   * to populate `primaryBranchCode` + `activeBranchCode` on app boot and
   * after every override write.
   *
   * 404/503 fallback (per design §6.4): callers should treat both as
   * "route not deployed yet" and keep the legacy `branchCode` from
   * `/v3/config/user-roles`. The HTTP layer surfaces those as `ApiError`
   * with `httpStatus` set; the calling AuthContext catches and downgrades.
   */
  public myContext(opts?: RequestOptions): Promise<MyContextResponse> {
    return this.http.get<MyContextResponse>('/v3/session/my-context', { ...opts });
  }

  /**
   * Persist a branch override for "today" (branch-local timezone, server-
   * computed). Idempotent for the same user/day — re-posting replaces.
   *
   * Body: `{ branchCode, reason? }`. Server validates branch existence +
   * `IsActive` + caller authz.
   */
  public setBranchOverride(
    req: SetBranchOverrideRequest,
    opts?: RequestOptions
  ): Promise<SetBranchOverrideResponse> {
    return this.http.post<SetBranchOverrideResponse>('/v3/session/branch-override', {
      ...opts,
      body: req,
    });
  }

  /**
   * Remove today's override row (if any) so the next read returns the
   * primary branch. Idempotent — 200 whether a row was deleted or not.
   */
  public clearBranchOverride(
    opts?: RequestOptions
  ): Promise<ClearBranchOverrideResponse> {
    return this.http.del<ClearBranchOverrideResponse>('/v3/session/branch-override', {
      ...opts,
    });
  }
}

export type {
  MyContextResponse,
  SetBranchOverrideRequest,
  SetBranchOverrideResponse,
  ClearBranchOverrideResponse,
};
